import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SearchCriteria } from "./Search";
import {
  MailboxAccountList,
  Rule,
  MailboxRuleStats,
  MailboxAccountListItem,
} from "@dashboard-v3/api";
import {
  Box,
  Button,
  TableBody,
  TableCell,
  TableCellProps,
  TableFooter,
  TablePagination,
  TableRow,
  Typography,
} from "@material-ui/core";
import ErrorDetails from "./ErrorDetails";
import formatNumber from "utils/formatNumber";
import getMailboxRuleStatus, {
  taskIsEnded,
} from "../../common/getMailboxRuleStatus";
import ErrorDetailsModal from "./ErrorDetailsModal";
import useSWR from "swr";
import { fetcher } from "utils/apiRequestWithErrorCode";
import refreshInterval from "../refreshInterval";

interface TableProps {
  actionType: Rule["actionType"];
  executionStatus: MailboxRuleStats["executionStatus"];
  ruleId: string;
  searchCriteria?: SearchCriteria;
  pageLimit: number;
}

export default function Body({
  actionType,
  executionStatus,
  ruleId,
  searchCriteria,
  pageLimit,
}: TableProps) {
  const { t } = useTranslation("stats");
  const [showErrorForAccount, setShowErrorForAccount] = useState<string>();
  const { accounts, setPage, accountsTotal, currentPage } =
    useFetchMailboxAccounts({
      ruleId,
      executionStatus,
      searchCriteria,
      limit: pageLimit,
    });

  const isEmpty = accounts && accounts.length === 0;

  const columns =
    actionType === "REPLACE_ATTACHMENTS" || actionType === "MOVE_EMAILS"
      ? 10
      : 9;

  const getPaginationLabel = context => {
    const { count, page } = context;
    const total = Math.ceil(count / pageLimit);
    return t("accounts.accountsTable.pagination.labels", {
      page: page + 1,
      total,
    });
  };

  return (
    <>
      <TableBody>
        {isEmpty && (
          <TableRow>
            <TableCell align="center" colSpan={columns}>
              <Typography>
                {t("rules:mailboxStats.thereAreNoAccountsToList")}
              </Typography>
            </TableCell>
          </TableRow>
        )}
        {accounts?.map(item => {
          const { stats } = item;
          const emailsWithErrorFatal = stats?.emailsWithErrorFatal ?? 0;
          const movedEmails =
            (stats?.emailsDeleted ?? 0) + (stats?.emailsMovedToTrash ?? 0);

          return (
            <TableRow key={item.emailAddress} data-testid="user-list__item">
              <TableCell key="email" align="left">
                {item.emailAddress}
              </TableCell>

              <StatusCell item={item} executionStatus={executionStatus} />

              <NumberCell
                key="messagesFiltered"
                number={stats?.messagesFiltered}
              />
              <NumberCell
                key="emailsDiscarded"
                number={stats?.emailsDiscarded}
              />
              <NumberCell
                key="emailsProcessed"
                number={stats?.emailsProcessed}
              />

              <NumberCell key="emailsUploaded" number={stats?.emailsUploaded} />

              {emailsWithErrorFatal > 0 ? (
                <TableCell align="center">
                  <>
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => setShowErrorForAccount(item.emailAddress)}
                    >
                      <Typography variant="body2">
                        {formatNumber(stats?.emailsWithErrorFatal)}
                      </Typography>
                      <Box ml={1} fontStyle="italic">
                        <Typography variant="caption">
                          {t("stats:accounts.accountsTable.showError")}
                        </Typography>
                      </Box>
                    </Button>
                  </>
                </TableCell>
              ) : (
                <NumberCell
                  key="emailsWithErrorFatal"
                  number={emailsWithErrorFatal}
                />
              )}

              <NumberCell
                key="emailsToReprocess"
                number={stats?.emailsToReprocess}
              />

              {actionType === "REPLACE_ATTACHMENTS" && (
                <NumberCell key="emailsUpdated" number={stats?.emailsUpdated} />
              )}
              {actionType === "MOVE_EMAILS" && (
                <NumberCell key="emailsUpdated" number={movedEmails} />
              )}
              <NumberCell key="filesUploaded" number={stats?.filesUploaded} />
            </TableRow>
          );
        })}
      </TableBody>
      <ErrorDetailsModal
        open={Boolean(showErrorForAccount)}
        ruleId={ruleId}
        account={showErrorForAccount}
        onClose={() => setShowErrorForAccount(null)}
      />
      <TableFooter>
        <TableRow>
          <TablePagination
            page={currentPage}
            count={accountsTotal}
            rowsPerPageOptions={[]}
            rowsPerPage={pageLimit}
            labelDisplayedRows={getPaginationLabel}
            onPageChange={(_, page) => setPage(page)}
            colSpan={columns}
          />
        </TableRow>
      </TableFooter>
    </>
  );
}

type FetchMailboxAccountOptions = {
  ruleId: string;
  limit: number;
  executionStatus: MailboxRuleStats["executionStatus"];
  searchCriteria?: SearchCriteria;
};

function useFetchMailboxAccounts(options: FetchMailboxAccountOptions) {
  const { limit, executionStatus, ruleId, searchCriteria } = options;
  const [page, setPage] = useState<number>(0);

  const query = new URLSearchParams();
  query.append("limit", limit.toString());
  query.append("page", page.toString());

  if (searchCriteria?.emailAccount) {
    query.append("emailAccount", searchCriteria.emailAccount);
  }

  if (searchCriteria?.status) {
    query.append("status", searchCriteria.status);
  }

  const { data } = useSWR<MailboxAccountList>(
    `/rules/${ruleId}/stats/accounts?${query.toString()}`,
    fetcher,
    {
      revalidateOnFocus: false,
      suspense: true,
      refreshInterval: () =>
        taskIsEnded(executionStatus) ? 0 : refreshInterval,
    }
  );

  return {
    accounts: data?.accounts,
    setPage,
    currentPage: data?.currentPage || 0,
    accountsTotal: data?.accountsTotal || 0,
  };
}

function StatusCell({
  executionStatus,
  item,
}: {
  item: MailboxAccountList["accounts"][number];
  executionStatus: MailboxRuleStats["executionStatus"];
}) {
  const { t } = useTranslation(["stats", "rules"]);
  const status = getStatus(item.executionStatus, executionStatus);

  return (
    <TableCell key="status" align="left">
      {item.error ? (
        <ErrorDetails
          status={t(`rules:mailboxStats.accountStatus.${status}`)}
          error={item.error}
        />
      ) : (
        t(`rules:mailboxStats.accountStatus.${status}`)
      )}
    </TableCell>
  );
}

function NumberCell({
  number,
  align = "center",
}: {
  key: string;
  number?: number;
  align?: TableCellProps["align"];
}) {
  return (
    <TableCell align={align}>
      <Typography variant="body2">{formatNumber(number ?? 0)}</Typography>
    </TableCell>
  );
}

function getStatus(
  accountStatus: MailboxAccountListItem["executionStatus"],
  ruleStatus: MailboxRuleStats["executionStatus"]
) {
  const ruleGroupedStatus = getMailboxRuleStatus(ruleStatus);

  if (ruleGroupedStatus === "STOPPED") {
    if (accountStatus === "FINISHED_WITH_ERROR") return accountStatus;
    return ruleGroupedStatus;
  }

  if (ruleGroupedStatus === "FINISHED") return "FINISHED";

  // In Confluence: https://mxhero.atlassian.net/wiki/spaces/D/pages/1462927361/Mailbox+stats#Status-de-la-tarea
  // In ticket: https://mxhero.atlassian.net/browse/MXH-5107
  // Seems to be different, so we keep the logic of the ticket
  // if (ruleGroupedStatus === "ACCOUNTS_WITH_ERRORS") return "RUNNING_WITH_ERROR";

  return accountStatus;
}
